import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getItems, getSections, updateItem } from '../services/api';
import api from '../services/api';
import '../App.css';

export default function InventoryPage({ labId, labName }) {
  const [tab, setTab]             = useState('items');
  const [items, setItems]         = useState([]);
  const [sections, setSections]   = useState([]);
  const [search, setSearch]       = useState('');
  const [sectionF, setSectionF]   = useState('');
  const [lowOnly, setLowOnly]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [editing, setEditing]     = useState(null);
  const [editForm, setEditForm]   = useState({});
  // Threshold bulk editor
  const [allItems, setAllItems]     = useState([]);
  const [tEdits, setTEdits]         = useState({});
  const [tSaving, setTSaving]       = useState(false);
  const [tDirty, setTDirty]         = useState({});
  // Upload sheet
  const [uploading, setUploading]   = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    getItems({ lab_id: labId || undefined, search: search || undefined, section_id: sectionF || undefined, low_stock: lowOnly || undefined })
      .then(r => setItems(r.data))
      .finally(() => setLoading(false));
  }, [labId, search, sectionF, lowOnly]);

  const loadAll = useCallback(() => {
    getItems({ lab_id: labId || undefined, section_id: sectionF || undefined }).then(r => {
      setAllItems(r.data);
      const init = {};
      r.data.forEach(item => {
        init[item.id] = { soft: item.soft_threshold ?? '', hard: item.hard_threshold ?? '', reorder: item.reorder_qty ?? '' };
      });
      setTEdits(init);
      setTDirty({});
    });
  }, [sectionF, labId]);

  useEffect(() => { getSections(labId).then(r => setSections(r.data)); }, [labId]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (tab === 'thresholds') loadAll(); }, [tab, loadAll]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!labId) { alert('Please select a lab first (use the Lab selector in the sidebar).'); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('sheet', file);
      fd.append('lab_id', labId);
      const { data } = await api.post('/items/upload-sheet', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setUploadResult({ success: true, data });
      load();
    } catch (err) {
      setUploadResult({ success: false, error: err?.response?.data?.error || err.message });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setEditForm({
      name:             item.name,
      vendor:           item.vendor || '',
      catalog_number:   item.catalog_number || '',
      barcode:          item.barcode || '',
      unit:             item.unit || 'units',
      soft_threshold:   item.soft_threshold ?? '',
      hard_threshold:   item.hard_threshold ?? '',
      reorder_qty:      item.reorder_qty ?? '',
      notes:            item.notes || '',
    });
  };

  const saveEdit = async () => {
    await updateItem(editing.id, editForm);
    setEditing(null);
    load();
  };

  const stockClass = (item) => {
    if (item.hard_threshold !== null && item.current_qty <= item.hard_threshold) return 'badge-critical';
    if (item.soft_threshold !== null && item.current_qty <= item.soft_threshold) return 'badge-low';
    return 'badge-ok';
  };

  const handleTChange = (id, field, val) => {
    setTEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
    setTDirty(prev => ({ ...prev, [id]: true }));
  };

  const saveThresholds = async () => {
    setTSaving(true);
    const dirtyIds = Object.keys(tDirty).filter(id => tDirty[id]);
    await Promise.all(dirtyIds.map(id => {
      const item = allItems.find(i => String(i.id) === String(id));
      if (!item) return Promise.resolve();
      return updateItem(id, {
        ...item,
        soft_threshold: tEdits[id].soft !== '' ? parseFloat(tEdits[id].soft) : null,
        hard_threshold: tEdits[id].hard !== '' ? parseFloat(tEdits[id].hard) : null,
        reorder_qty:    tEdits[id].reorder !== '' ? parseFloat(tEdits[id].reorder) : null,
      });
    }));
    setTDirty({});
    setTSaving(false);
    loadAll();
  };

  const dirtyCount = Object.values(tDirty).filter(Boolean).length;

  const thresholdStatus = (item, edits) => {
    const soft = parseFloat(edits?.soft);
    const hard = parseFloat(edits?.hard);
    const qty  = parseFloat(item.current_qty);
    if (!isNaN(hard) && qty <= hard) return { label: '🔴 Critical', cls: 'badge-critical' };
    if (!isNaN(soft) && qty <= soft) return { label: '🟡 Low',      cls: 'badge-low' };
    return { label: '🟢 OK', cls: 'badge-ok' };
  };

  const tabs = [
    { id: 'items',      label: '🧪 All Items' },
    { id: 'thresholds', label: '⚙️ Thresholds' },
    { id: 'upload',     label: '⬆️ Upload Sheet' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Inventory</div>
          <div className="page-sub">{items.length} items</div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #e2e8f0' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
            color: tab === t.id ? '#0A2A5E' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #0A2A5E' : '2px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── All Items tab ── */}
      {tab === 'items' && (
        <>
          <div className="search-row">
            <input className="search-input" placeholder="Search by name, barcode, catalog #…" value={search} onChange={e => setSearch(e.target.value)} />
            <select className="filter-select" value={sectionF} onChange={e => setSectionF(e.target.value)}>
              <option value="">All Sections</option>
              {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} />
              Low stock only
            </label>
          </div>

          {loading ? (
            <div className="loading">Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🧪</div><div>No items found</div></div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Pycube ID</th>
                      <th>Item Name</th>
                      <th>Section</th>
                      <th>Location</th>
                      <th>Qty</th>
                      <th>Thresholds</th>
                      <th>Nearest Expiry</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id}>
                        <td><span className="pycube-id">{item.pycube_id || '—'}</span></td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{item.name}</div>
                          {item.vendor && <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.vendor}</div>}
                          {item.catalog_number && <div style={{ fontSize: 11, color: '#94a3b8' }}>Cat# {item.catalog_number}</div>}
                        </td>
                        <td>{item.section_name}</td>
                        <td>{item.location_name || '—'}</td>
                        <td><b>{item.current_qty}</b> <span style={{ color: '#94a3b8' }}>{item.unit}</span></td>
                        <td style={{ fontSize: 12 }}>
                          {item.soft_threshold !== null ? <span>⚠️ {item.soft_threshold}</span> : '—'}
                          {item.hard_threshold !== null ? <span style={{ marginLeft: 4 }}>🔴 {item.hard_threshold}</span> : ''}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {item.nearest_expiry
                            ? <span style={{ color: new Date(item.nearest_expiry) < new Date(Date.now() + 30*86400000) ? '#d97706' : '#374151' }}>
                                {item.nearest_expiry.slice(0,10)}
                              </span>
                            : '—'}
                        </td>
                        <td><span className={`badge ${stockClass(item)}`}>{stockClass(item) === 'badge-critical' ? '🔴 Critical' : stockClass(item) === 'badge-low' ? '🟡 Low' : '🟢 OK'}</span></td>
                        <td><button className="btn btn-outline btn-sm" onClick={() => openEdit(item)}>Edit</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Thresholds bulk editor tab ── */}
      {tab === 'thresholds' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                Set soft (warning) and hard (critical/reorder) thresholds for each item.
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                ⚠️ Soft = plan to reorder · 🔴 Hard = immediate reorder needed
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <select className="filter-select" value={sectionF} onChange={e => { setSectionF(e.target.value); }}>
                <option value="">All Sections</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {dirtyCount > 0 && (
                <button className="btn btn-primary" onClick={saveThresholds} disabled={tSaving}>
                  {tSaving ? '⏳ Saving…' : `Save ${dirtyCount} change${dirtyCount !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Section</th>
                    <th>Current Qty</th>
                    <th>⚠️ Soft Threshold</th>
                    <th>🔴 Hard Threshold</th>
                    <th>Reorder Qty</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.map(item => {
                    const e = tEdits[item.id] || {};
                    const dirty = tDirty[item.id];
                    const st = thresholdStatus(item, e);
                    return (
                      <tr key={item.id} style={{ background: dirty ? '#fffbeb' : 'white' }}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{item.name}</div>
                          {item.catalog_number && <div style={{ fontSize: 11, color: '#94a3b8' }}>Cat# {item.catalog_number}</div>}
                        </td>
                        <td style={{ fontSize: 12 }}>{item.section_name}</td>
                        <td style={{ fontWeight: 700 }}>{item.current_qty} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{item.unit}</span></td>
                        <td>
                          <input
                            type="number"
                            value={e.soft ?? ''}
                            onChange={ev => handleTChange(item.id, 'soft', ev.target.value)}
                            style={{ width: 72, padding: '5px 8px', borderRadius: 6, border: '1.5px solid #e2e8f0', fontSize: 13 }}
                            placeholder="—"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={e.hard ?? ''}
                            onChange={ev => handleTChange(item.id, 'hard', ev.target.value)}
                            style={{ width: 72, padding: '5px 8px', borderRadius: 6, border: '1.5px solid #e2e8f0', fontSize: 13 }}
                            placeholder="—"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={e.reorder ?? ''}
                            onChange={ev => handleTChange(item.id, 'reorder', ev.target.value)}
                            style={{ width: 72, padding: '5px 8px', borderRadius: 6, border: '1.5px solid #e2e8f0', fontSize: 13 }}
                            placeholder="—"
                          />
                        </td>
                        <td>
                          <span className={`badge ${st.cls}`}>{st.label}</span>
                          {dirty && <span style={{ fontSize: 10, color: '#d97706', marginLeft: 4 }}>●</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload Sheet tab ── */}
      {tab === 'upload' && (
        <div>
          <div className="card">
            <div className="card-title">⬆️ Upload Inventory Sheet</div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.6 }}>
              Upload an Excel (.xlsx) or CSV file to bulk-import inventory items into <b>{labName || 'the selected lab'}</b>.
              The system auto-detects columns for item name, category/section, quantity, unit, vendor, catalog number, thresholds, etc.
            </p>

            {!labId && (
              <div style={{ padding: '12px 16px', background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
                ⚠️ Please select a lab from the sidebar before uploading.
              </div>
            )}

            <div
              style={{
                border: '2px dashed #cbd5e1', borderRadius: 12, padding: '40px 24px',
                textAlign: 'center', cursor: labId ? 'pointer' : 'not-allowed',
                background: uploading ? '#f8fafc' : '#fff',
                opacity: labId ? 1 : 0.5,
              }}
              onClick={() => labId && !uploading && fileInputRef.current?.click()}
            >
              <div style={{ fontSize: 40, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0A2A5E', marginBottom: 4 }}>
                {uploading ? 'Uploading…' : 'Click to choose a file'}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Supports .xlsx and .csv</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleUpload}
              />
            </div>

            {uploading && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <div style={{ fontSize: 13, color: '#64748b' }}>Processing file — this may take a moment…</div>
                <div style={{ marginTop: 8, height: 4, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: '#0A2A5E', width: '60%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
              </div>
            )}

            {uploadResult && (
              <div style={{
                marginTop: 20, padding: '16px 20px', borderRadius: 10,
                background: uploadResult.success ? '#f0fdf4' : '#fef2f2',
                border: `1.5px solid ${uploadResult.success ? '#86efac' : '#fca5a5'}`,
              }}>
                {uploadResult.success ? (
                  <>
                    <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 8 }}>
                      ✓ Import successful!
                    </div>
                    <div style={{ fontSize: 13, color: '#374151' }}>
                      <b>{uploadResult.data?.inserted ?? 0}</b> items imported,&nbsp;
                      <b>{uploadResult.data?.skipped ?? 0}</b> skipped (duplicates).
                    </div>
                    {uploadResult.data?.detected_columns && (
                      <div style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>
                        Detected columns: {Object.entries(uploadResult.data.detected_columns)
                          .map(([k, v]) => `${k} → "${v}"`)
                          .join(', ')}
                      </div>
                    )}
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: 12 }}
                      onClick={() => { setUploadResult(null); setTab('items'); }}
                    >
                      View Items →
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>Upload failed</div>
                    <div style={{ fontSize: 13, color: '#374151' }}>{uploadResult.error}</div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title">💡 Tips for best results</div>
            <ul style={{ fontSize: 13, color: '#475569', lineHeight: 2, paddingLeft: 20, margin: 0 }}>
              <li>Column names don't need to be exact — the system uses fuzzy matching</li>
              <li>Common column names: <code>Item Name</code>, <code>Category</code>, <code>Section</code>, <code>Quantity</code>, <code>Unit</code>, <code>Vendor</code>, <code>Catalog #</code></li>
              <li>Optional: <code>Soft Threshold</code>, <code>Hard Threshold</code>, <code>Reorder Qty</code>, <code>Notes</code></li>
              <li>Items in the same <code>Category</code> / <code>Section</code> column will be grouped together</li>
              <li>Duplicate item names in the same section are skipped automatically</li>
            </ul>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Edit Item</div>
              <button className="modal-close" onClick={() => setEditing(null)}>✕</button>
            </div>
            <div className="form-grid">
              <div className="form-field full">
                <label className="form-label">Item Name</label>
                <input className="form-input" value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} />
              </div>
              <div className="form-field">
                <label className="form-label">Vendor</label>
                <input className="form-input" value={editForm.vendor} onChange={e => setEditForm(f => ({...f, vendor: e.target.value}))} />
              </div>
              <div className="form-field">
                <label className="form-label">Catalog #</label>
                <input className="form-input" value={editForm.catalog_number} onChange={e => setEditForm(f => ({...f, catalog_number: e.target.value}))} />
              </div>
              <div className="form-field">
                <label className="form-label">Barcode</label>
                <input className="form-input" value={editForm.barcode} onChange={e => setEditForm(f => ({...f, barcode: e.target.value}))} />
              </div>
              <div className="form-field">
                <label className="form-label">Unit</label>
                <input className="form-input" value={editForm.unit} onChange={e => setEditForm(f => ({...f, unit: e.target.value}))} />
              </div>
              <div className="form-field">
                <label className="form-label">⚠️ Soft Threshold</label>
                <input className="form-input" type="number" value={editForm.soft_threshold} onChange={e => setEditForm(f => ({...f, soft_threshold: e.target.value}))} />
              </div>
              <div className="form-field">
                <label className="form-label">🔴 Hard Threshold</label>
                <input className="form-input" type="number" value={editForm.hard_threshold} onChange={e => setEditForm(f => ({...f, hard_threshold: e.target.value}))} />
              </div>
              <div className="form-field">
                <label className="form-label">Reorder Qty</label>
                <input className="form-input" type="number" value={editForm.reorder_qty} onChange={e => setEditForm(f => ({...f, reorder_qty: e.target.value}))} />
              </div>
              <div className="form-field full">
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={2} value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
